import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Radar, Info, ChevronDown, ChevronUp, FileText } from "lucide-react";

const IMP_BG = { Critical: "bg-red-50 text-red-700 border-red-200", High: "bg-amber-50 text-amber-700 border-amber-200", Moderate: "bg-indigo-50 text-indigo-700 border-indigo-200", Low: "bg-slate-50 text-slate-500 border-slate-200" };
const REL_CLS = {
  "Supports the observation": "bg-emerald-50 text-emerald-700 border-emerald-200",
  "Partially supports the observation": "bg-teal-50 text-teal-700 border-teal-200",
  "Contradicts the observation": "bg-red-50 text-red-700 border-red-200",
  "Provides clinical context only": "bg-slate-50 text-slate-500 border-slate-200",
  "Identifies an evidence gap": "bg-amber-50 text-amber-700 border-amber-200",
  "Source of the statement": "bg-indigo-50 text-indigo-700 border-indigo-200",
};
const LIFECYCLE = ["Detected", "Source verification", "Initial scientific triage", "Corroboration required", "Under expert review", "Validated signal", "Incorporated into intelligence", "Monitoring", "Closed or downgraded"];
const TABS = [["overview", "Signal Overview"], ["register", "Signal Register"], ["workspace", "Validation Workspace"], ["monitoring", "Monitoring & Triggers"]];
const IMP_ORDER = { Critical: 0, High: 1, Moderate: 2, Low: 3 };

const Dim = ({ l, v, cls }) => (
  <span className={`text-[9px] font-mono uppercase tracking-wide border rounded-full px-1.5 py-0.5 ${cls || "bg-slate-50 text-slate-500 border-slate-200"}`}>{l}: {v}</span>
);

function EvidenceDrawer({ links }) {
  const [openId, setOpenId] = useState(null);
  return (
    <div className="space-y-1.5 mt-2">
      {links.map((e) => (
        <div key={e.eid} className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5">
          <button onClick={() => setOpenId(openId === e.eid ? null : e.eid)} className="w-full flex items-start gap-2 text-left" data-testid={`evidence-link-${e.eid}`}>
            <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
            <span className="flex-1 min-w-0">
              <span className="text-[11px] font-semibold text-slate-700 leading-snug block">{e.title}</span>
              <span className={`inline-block text-[9px] font-mono uppercase tracking-wide border rounded-full px-1.5 py-0.5 mt-1 ${REL_CLS[e.relationship] || REL_CLS["Provides clinical context only"]}`}>{e.relationship}</span>
            </span>
            {openId === e.eid ? <ChevronUp className="w-3 h-3 text-slate-400 shrink-0 mt-1" /> : <ChevronDown className="w-3 h-3 text-slate-400 shrink-0 mt-1" />}
          </button>
          {openId === e.eid && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1.5 mt-2 pt-2 border-t border-slate-200/70 text-[10.5px] text-slate-600" data-testid={`evidence-detail-${e.eid}`}>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Source ID</span>{e.eid}</p>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Source type</span>{e.type}</p>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Publication / presentation date</span>{e.date}</p>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Access classification</span>{e.access}</p>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Verification status</span><span className={e.verification.includes("pending") ? "text-amber-700" : ""}>{e.verification}</span></p>
              <p><span className="font-mono text-[9px] uppercase text-slate-400 block">Relationship to the signal</span>{e.relationship}</p>
              <p className="sm:col-span-2"><span className="font-mono text-[9px] uppercase text-slate-400 block">Relevant excerpt / source location</span>{e.excerpt}</p>
              <p className="sm:col-span-2"><span className="font-mono text-[9px] uppercase text-slate-400 block">Reviewer comments</span>{e.reviewer_comment}</p>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function SignalCard({ s, insights, onSelect }) {
  const [drawer, setDrawer] = useState(null);
  const linked = insights.filter((i) => s.insight_ids.includes(i.id));
  const toggle = (d) => setDrawer(drawer === d ? null : d);
  return (
    <div className="bg-white border border-slate-200/80 rounded-xl p-5 shadow-sm" data-testid={`signal-card-${s.id}`}>
      <div className="flex flex-wrap gap-1.5 mb-2">
        <Badge variant="outline" className="text-[9px] bg-amber-50 text-amber-700 border-amber-200">{s.classification}</Badge>
        <Badge variant="outline" className={`text-[9px] ${IMP_BG[s.importance]}`}>Importance: {s.importance}</Badge>
        <span className="text-[9px] font-mono uppercase text-amber-600/80 ml-auto">Illustrative demonstration data</span>
      </div>
      <h4 className="text-sm font-bold text-slate-900 leading-snug">{s.title}</h4>
      <p className="text-xs text-slate-600 leading-relaxed mt-1.5">{s.synthesis}</p>
      <p className="text-[11px] text-slate-500 mt-1"><span className="font-mono text-[9px] uppercase text-slate-400 mr-1">Why it may matter</span>{s.why}</p>
      <div className="flex flex-wrap gap-1.5 mt-2.5" data-testid={`signal-dims-${s.id}`}>
        <Dim l="Novelty" v={s.novelty} />
        <Dim l="Corroboration" v={s.corroboration} cls={s.corroboration === "Single observation" ? "bg-amber-50 text-amber-700 border-amber-200" : undefined} />
        <Dim l="Evidence" v={s.evidence_rel} />
        <Dim l="Confidence" v={s.confidence} />
        <Dim l="Verification" v={s.verification} cls="bg-amber-50 text-amber-700 border-amber-200" />
        <Dim l="Review" v={s.review} cls="bg-violet-50 text-violet-700 border-violet-300" />
      </div>
      <p className="text-[10px] text-slate-500 mt-1.5">Confidence reason: {s.confidence_reason}</p>
      {s.corroboration === "Single observation" && <p className="text-[10px] font-semibold text-amber-700 mt-1">Single observation — corroboration required.</p>}
      {s.contradictory_obs && <p className="text-[10px] text-red-700 mt-1"><span className="font-semibold">Contradictory observations recorded</span> — see Validation Workspace.</p>}
      <p className="text-[10.5px] text-slate-500 mt-2">
        <span className="font-semibold text-slate-700">{s.voices} independent voices</span> · {s.vsources} sources · {s.source_types} · {s.institutions} institutions (deduplicated) · {s.geos} · {s.segments} · observed {s.first_seen} → {s.last_seen} · within cut-off 2026-06-14
      </p>
      <p className="text-[10px] text-slate-400 mt-1.5">Lifecycle: <span className="font-semibold text-teal-700">{s.stage}</span> ({LIFECYCLE.indexOf(s.stage) + 1}/9) · Reviewer: {s.owner} · Last review: {s.last_review} · Next step: {s.next_step}</p>
      <div className="flex flex-wrap items-center gap-3 mt-2.5 pt-2.5 border-t border-slate-100">
        <button onClick={() => toggle("statements")} className="flex items-center gap-1 text-[11px] font-semibold text-teal-700 hover:text-teal-900" data-testid={`signal-statements-${s.id}`}>
          Supporting statements ({linked.length}) {drawer === "statements" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        <button onClick={() => toggle("evidence")} className="flex items-center gap-1 text-[11px] font-semibold text-slate-600 hover:text-slate-900" data-testid={`signal-evidence-${s.id}`}>
          Supporting evidence ({(s.evidence_links || []).length}) {drawer === "evidence" ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
        </button>
        {onSelect && <button onClick={() => onSelect(s.id)} className="text-[11px] font-semibold text-indigo-700 hover:text-indigo-900 ml-auto" data-testid={`signal-workspace-${s.id}`}>Open in Validation Workspace →</button>}
      </div>
      {drawer === "statements" && (
        <div className="space-y-2 mt-2" data-testid={`signal-statements-list-${s.id}`}>
          {linked.map((i) => (
            <div key={i.id} className="bg-slate-50 border border-slate-200/80 rounded-lg p-2.5">
              <p className="text-[11px] text-slate-700 leading-snug">{i.quote}</p>
              <p className="text-[9.5px] text-slate-400 mt-1">Example {i.hcp_role?.toLowerCase()} · {i.country} · {i.date} · <span className="text-violet-600">AI-generated paraphrase — verification pending</span> · {i.id}</p>
            </div>
          ))}
          {linked.length === 0 && <p className="text-[10.5px] text-slate-400">Statements available in HCP Voice.</p>}
        </div>
      )}
      {drawer === "evidence" && <EvidenceDrawer links={s.evidence_links || []} />}
    </div>
  );
}

const REG_FILTERS = [
  ["importance", "Importance"], ["classification", "Classification"], ["corroboration", "Corroboration"],
  ["confidence", "Confidence"], ["verification", "Verification"], ["review", "Review status"],
];

export default function EmergingSignals() {
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("overview");
  const [sel, setSel] = useState("sg1");
  const [regF, setRegF] = useState({});
  const [regSort, setRegSort] = useState("importance");

  useEffect(() => { api.get("/signals").then((r) => setData(r.data)); }, []);
  const clusters = data?.clusters || [];

  const regRows = useMemo(() => {
    let rows = clusters.filter((s) => REG_FILTERS.every(([k]) => !regF[k] || s[k] === regF[k]));
    if (regSort === "importance") rows = [...rows].sort((a, b) => IMP_ORDER[a.importance] - IMP_ORDER[b.importance]);
    if (regSort === "recent") rows = [...rows].sort((a, b) => b.last_seen.localeCompare(a.last_seen));
    if (regSort === "title") rows = [...rows].sort((a, b) => a.title.localeCompare(b.title));
    return rows;
  }, [clusters, regF, regSort]);

  if (!data) return <div className="p-8 text-slate-400 text-sm">Loading signal register…</div>;
  const selSig = clusters.find((c) => c.id === sel);
  const openWs = (id) => { setSel(id); setTab("workspace"); };
  const nearCutoff = clusters.filter((c) => c.last_seen >= "2026-05-31").length;

  return (
    <div className="space-y-4" data-testid="signals-view">
      <div className="bg-slate-900 rounded-xl p-5 border border-slate-800">
        <div className="flex items-center gap-2">
          <Radar className="w-5 h-5 text-teal-400" />
          <h3 className="font-heading text-base font-bold text-white">Early-warning signal register — governed, not established</h3>
        </div>
        <p className="text-sm text-slate-400 mt-1">Potential new or under-recognised observations prioritised for scientific review, corroboration and monitoring.</p>
        <p className="text-[11px] text-slate-500 mt-2 flex items-start gap-1.5"><Info className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
          Potential emerging signals are not established findings. Classification reflects the current evidence cut-off (2026-06-14) and may change after source verification, expert review or additional corroboration. This single cross-sectional assessment does not support "strengthening" or "growing" labels; statuses simulate the intended workflow on demonstration content.</p>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {TABS.map(([id, label]) => (
          <button key={id} data-testid={`signals-tab-${id}`} onClick={() => setTab(id)}
            className={`text-[11px] font-semibold rounded-full px-3.5 py-1.5 border transition-colors ${tab === id ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-teal-400 hover:text-teal-700"}`}>{label}</button>
        ))}
      </div>

      {tab === "overview" && (
        <div className="space-y-4">
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm flex flex-wrap gap-x-6 gap-y-1.5" data-testid="signals-coverage">
            {[["Potential signals", clusters.length], ["Awaiting corroboration", clusters.filter((c) => c.corroboration !== "Multiple independent voices").length],
              ["Under expert review", clusters.filter((c) => c.review === "Under scientific review").length],
              ["With contradictory evidence", clusters.filter((c) => c.contradictory_obs).length],
              ["Approaching evidence cut-off", nearCutoff],
              ["Geographic coverage", "US, DE, FR, UK + congress"], ["All within evidence cut-off", "Yes"]].map(([l, v]) => (
              <span key={l} className="text-[10px] font-mono uppercase tracking-widest text-slate-400">{l}: <span className="text-slate-800 font-bold">{v}</span></span>
            ))}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {clusters.map((s) => <SignalCard key={s.id} s={s} insights={data.signals} onSelect={openWs} />)}
          </div>
        </div>
      )}

      {tab === "register" && (
        <div className="space-y-3">
          <div className="bg-white border border-slate-200/80 rounded-xl p-3 shadow-sm flex flex-wrap items-end gap-2" data-testid="register-filters">
            {REG_FILTERS.map(([k, label]) => {
              const opts = [...new Set(clusters.map((c) => c[k]))];
              return (
                <label key={k} className="text-[9px] font-mono uppercase tracking-wide text-slate-400">
                  {label}
                  <select value={regF[k] || ""} onChange={(e) => setRegF({ ...regF, [k]: e.target.value || undefined })}
                    data-testid={`register-filter-${k}`}
                    className="block mt-0.5 text-[11px] font-sans normal-case tracking-normal text-slate-700 border border-slate-200 rounded-md px-2 py-1 bg-white">
                    <option value="">All</option>
                    {opts.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                </label>
              );
            })}
            <label className="text-[9px] font-mono uppercase tracking-wide text-slate-400">
              Sort by
              <select value={regSort} onChange={(e) => setRegSort(e.target.value)} data-testid="register-sort"
                className="block mt-0.5 text-[11px] font-sans normal-case tracking-normal text-slate-700 border border-slate-200 rounded-md px-2 py-1 bg-white">
                <option value="importance">Strategic importance</option>
                <option value="recent">Most recent observation</option>
                <option value="title">Title (A–Z)</option>
              </select>
            </label>
            {Object.values(regF).some(Boolean) && (
              <button onClick={() => setRegF({})} className="text-[11px] font-semibold text-teal-700 hover:underline pb-1" data-testid="register-clear-filters">Clear filters</button>
            )}
            <span className="text-[10px] text-slate-400 ml-auto pb-1">{regRows.length} of {clusters.length} signals</span>
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm overflow-x-auto" data-testid="signal-register">
            <table className="w-full text-xs">
              <thead><tr className="text-left border-b border-slate-200">
                {["Signal", "Classification", "Importance", "Corroboration", "Evidence", "Confidence", "Verification", "Review", "Stage", "Last observed", ""].map((h) => (
                  <th key={h} className="py-2 pr-3 font-mono text-[9px] uppercase tracking-widest text-slate-400">{h}</th>))}
              </tr></thead>
              <tbody>
                {regRows.map((s) => (
                  <tr key={s.id} className="border-b border-slate-100 align-top hover:bg-slate-50/60">
                    <td className="py-2.5 pr-3 font-semibold text-slate-800 max-w-[240px]">{s.title}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{s.classification}</td>
                    <td className="py-2.5 pr-3"><span className={`text-[10px] border rounded-full px-2 py-0.5 ${IMP_BG[s.importance]}`}>{s.importance}</span></td>
                    <td className="py-2.5 pr-3 text-slate-600">{s.corroboration} ({s.voices}v/{s.vsources}s)</td>
                    <td className="py-2.5 pr-3 text-slate-600">{s.evidence_rel}</td>
                    <td className="py-2.5 pr-3 text-slate-600">{s.confidence}</td>
                    <td className="py-2.5 pr-3 text-amber-700">{s.verification}</td>
                    <td className="py-2.5 pr-3 text-violet-700">{s.review}</td>
                    <td className="py-2.5 pr-3 text-teal-700">{s.stage}</td>
                    <td className="py-2.5 pr-3 text-slate-500">{s.last_seen}</td>
                    <td className="py-2.5"><button onClick={() => openWs(s.id)} className="text-teal-700 font-semibold hover:underline" data-testid={`register-open-${s.id}`}>Open →</button></td>
                  </tr>
                ))}
                {regRows.length === 0 && <tr><td colSpan={11} className="py-6 text-center text-slate-400 text-[11px]">No signals match the current filters.</td></tr>}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === "workspace" && selSig && (
        <div className="space-y-3" data-testid="validation-workspace">
          <div className="flex flex-wrap gap-1.5">
            {clusters.map((c) => (
              <button key={c.id} onClick={() => setSel(c.id)} className={`text-[10.5px] font-semibold rounded-full px-3 py-1 border ${sel === c.id ? "bg-slate-900 text-teal-300 border-slate-900" : "bg-white text-slate-500 border-slate-200"}`} data-testid={`ws-select-${c.id}`}>{c.id.toUpperCase()}</button>
            ))}
          </div>
          <SignalCard s={selSig} insights={data.signals} />
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm grid grid-cols-1 md:grid-cols-2 gap-x-5 gap-y-2.5 text-[11px] text-slate-600">
            {[["Contradictory observations", selSig.contradictory_obs || "None recorded within the current evidence base."],
              ["Source-diversity assessment", `${selSig.institutions} institutions · ${selSig.source_types} · ${selSig.geos} — ${selSig.institutions < 3 ? "low diversity (limitation)" : "acceptable for triage"}`],
              ["Known limitations", selSig.confidence_reason],
              ["Reviewer rationale", "Proposed classification pending completion of source verification; conservative gating applied."],
              ["Proposed classification", selSig.classification],
              ["Reviewer controls", "Approve · Revise · Downgrade (available to assigned reviewer in Admin & Review)"]].map(([l, v]) => (
              <div key={l}><p className="font-mono text-[9px] uppercase tracking-wide text-slate-400">{l}</p><p>{v}</p></div>
            ))}
          </div>
          <div className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm" data-testid="signal-audit">
            <p className="font-mono text-[9px] uppercase tracking-widest text-slate-400 mb-2">Audit history · Last review {selSig.last_review}</p>
            <div className="space-y-1.5">
              {(selSig.audit || []).map((a, idx) => (
                <p key={idx} className="text-[11px] text-slate-600"><span className="font-mono text-[10px] text-slate-400 mr-2">{a.date}</span><span className="font-semibold text-slate-700 mr-1">{a.actor}:</span>{a.action}</p>
              ))}
            </div>
            <p className="text-[10px] text-slate-400 mt-2">Unreviewed signals do not flow into approved reports or final strategic recommendations.</p>
          </div>
        </div>
      )}

      {tab === "monitoring" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="signals-monitoring">
          {clusters.map((s) => (
            <div key={s.id} className="bg-white border border-slate-200/80 rounded-xl p-4 shadow-sm">
              <p className="text-xs font-bold text-slate-900">{s.title}</p>
              <div className="grid grid-cols-1 gap-1.5 mt-2 text-[10.5px] text-slate-600">
                <p><span className="text-emerald-700 font-semibold">Would validate:</span> {s.monitor_validate}</p>
                <p><span className="text-amber-700 font-semibold">Would weaken/close:</span> {s.monitor_weaken}</p>
                <p><span className="text-slate-400">Monitoring trigger:</span> {s.trigger}</p>
                <p><span className="text-indigo-700 font-semibold">Watchlist:</span> {s.watch} — excluded from the current cross-sectional evidence base</p>
                <p className="text-slate-400">Owner: {s.owner} · Review deadline: {s.deadline} · Congress-triggered or scheduled reassessment available via Admin</p>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
